import styled from 'styled-components';
import { css } from '@styled-system/css';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';

import Dripicon from 'humanity/components/dripicon';
import Flex from 'humanity/primitives/flex';
import { paginationProps } from 'humanity/components/pagination/pagination.types.js';
import Button from 'humanity/primitives/button';
import VisuallyHidden from 'humanity/components/visuallyHidden';

const StyledPagination = styled(Flex)(
  css({ gridColumnStart: '4', gridColumnEnd: '13', justifyContent: 'center' })
);
const StyledButton = styled(Button)(({ hide }) =>
  css({
    border: '3px solid',
    display: hide ? 'none' : 'inline-block',
  })
);

const StyledIcon = styled(Dripicon)(css({ marginLeft: '-2px' }));

const Pagination = ({ totalPages }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  if (totalPages === 0) {
    return null;
  }

  const currentPage = Number(searchParams.get('page')) || 1;
  const calculatedPage = currentPage - 2;
  const startPage = Math.max(1, calculatedPage);
  const endPage =
    totalPages > 5 && startPage < 2
      ? Math.min(totalPages, startPage + 4)
      : Math.min(totalPages, currentPage + 2);

  const createPageURL = (pageNumber) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNumber.toString());
    return `${pathname}?${params.toString()}`;
  };

  return (
    <StyledPagination aria-label="pagination" as="nav">
      <Link href={createPageURL(currentPage - 1)} shallow>
        <StyledButton type="button" variant="circle" hide={currentPage === 1}>
          <StyledIcon icon="ChevronLeft" title="Previous page" />
        </StyledButton>
      </Link>
      {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
        const pageNumber = startPage + i;
        const isCurrentPage = currentPage === pageNumber;
        return (
          <Link key={pageNumber} href={createPageURL(pageNumber)} shallow>
            <Button
              type="button"
              variant="circle"
              active={isCurrentPage ? 'active' : undefined}
              aria-current={isCurrentPage ? 'page' : undefined}
            >
              <VisuallyHidden>Page </VisuallyHidden>
              {pageNumber}
            </Button>
          </Link>
        );
      })}
      <Link href={createPageURL(currentPage + 1)} shallow>
        <StyledButton type="button" variant="circle" hide={currentPage === totalPages}>
          <StyledIcon icon="ChevronRight" title="Next page" />
        </StyledButton>
      </Link>
    </StyledPagination>
  );
};

Pagination.propTypes = {
  ...paginationProps,
};

export default Pagination;
